"use client";

import { useGlobalModalContext } from "@components/GlobalModalProvider/GlobalModalProvider";
import { Portal } from "@ecco/ui/portal";
import { defaultUnderlineStyle } from "@ecco/ui/css/common";
import { useDisclosure } from "@ecco/ui/use-disclosure";
import {
    animatedContainerMobileStyles,
    toggleNavigationButtonStyles,
} from "@components/layouts/navigation/styles";
import type { NavigationModel } from "@lib/contentful/mappers/mapNavigation";
import { useAuth } from "@lib/auth/useAuth";
import { CommonCMS } from "@lib/constants/contentful";
import ClickAndCollect from "@lib/constants/contentful/clickAndCollect";
import {
    customerServicePages,
    getPagePathByLocale,
    myAccountPage,
    storeLocatorPage,
} from "@lib/constants/pagePaths";
import { ModalTypes } from "@lib/enums/ModalTypes";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { IconButton } from "@ecco/ui/icon-button";
import { Box } from "@ecco/ui/box";
import { Flex } from "@ecco/ui/flex";
import { ECCOIcon } from "@ecco/ui/ecco-icon";
import { NextLink } from "@components/ui/next-link/NextLink";
import { motion } from "framer-motion";
import { useEffect } from "react";
import { sizes, space } from "@ecco/ui/tokens";
import { MobileBottomContainer } from "./MobileBottomContainer";
import { MobileNav } from "./MobileNav";
import { TOGGLE_NAVIGATION_BUTTON } from "@e2e/helpers/navigation/constants";
import { useLocale } from "next-intl";
import { useRouteChange } from "@store/hooks/useRouteChange";
import { fontWeights } from "@ecco/ui/tokens/typography";

interface MobileNavigationContainerProps {
    navigation: NavigationModel;
    searchBarOpen: boolean;
}

const DROPDOWN_AREA_ID = "mobile-dropdown-menu";

const linkSx = {
    _hover: {
        "--b": "100%",
        color: "black",
        ...defaultUnderlineStyle,
    },
    _focus: {
        "--b": "100%",
        color: "black",
        ...defaultUnderlineStyle,
    },
    _active: {
        textDecoration: "none",
        color: "black",
    },
};

export const MobileNavigationContainer = ({
    navigation,
    searchBarOpen,
}: MobileNavigationContainerProps) => {
    const { isOpen, getButtonProps, onClose } = useDisclosure();
    const { getMultiple } = useMicrocopy();

    const locale = useLocale();
    const { isLoggedIn, session } = useAuth();
    const { showModal } = useGlobalModalContext();

    const handleAccountClick = () => {
        onClose();
        showModal(ModalTypes.createAccount);
    };

    const microcopies = getMultiple(CommonCMS.shared, [
        CommonCMS.myAccount,
        CommonCMS.help,
        ClickAndCollect.stores,
    ]);

    useEffect(() => {
        if (searchBarOpen && isOpen) {
            onClose();
        }
    }, [isOpen, onClose, searchBarOpen]);

    useRouteChange(() => {
        if (isOpen) {
            onClose();
        }
    });

    const {
        "aria-hidden": _ariaHidden,
        "aria-expanded": _ariaExpanded,
        "aria-controls": _ariaControls,
        ...buttonsProps
    } = getButtonProps();

    return (
        <>
            <Flex gap={3} alignItems="center">
                <IconButton
                    variant="tertiary"
                    aria-label="Toggle Navigation"
                    aria-expanded={isOpen}
                    aria-controls={DROPDOWN_AREA_ID}
                    h={sizes.navBarCollapsedHeight}
                    w="9" // Accessibility requires a minimum touch target of 44x44px
                    bgColor="white"
                    data-testid={TOGGLE_NAVIGATION_BUTTON}
                    sx={toggleNavigationButtonStyles}
                    icon={
                        isOpen ? (
                            <ECCOIcon
                                color="black"
                                data-testid="modalstatebutton,closemodal"
                                aria-label="Close"
                                name="close"
                            />
                        ) : (
                            <ECCOIcon
                                color="black"
                                data-testid="modalstatebutton,openmodal"
                                aria-label="menu"
                                name="menu"
                            />
                        )
                    }
                    {...buttonsProps}
                />
            </Flex>

            <>
                <motion.div
                    initial={false}
                    animate={{ height: isOpen ? "85vh" : 0 }}
                    transition={{ ease: [0.85, 0, 0.15, 1], duration: 0.5 }}
                    style={animatedContainerMobileStyles}
                    aria-hidden={!isOpen}
                    tabIndex={isOpen ? undefined : -1}
                    id={DROPDOWN_AREA_ID}
                    onClick={(e) => {
                        const a = (e.target as Element | null)?.closest?.("a");
                        if (
                            a instanceof HTMLAnchorElement &&
                            a.pathname === window.location.pathname
                        ) {
                            onClose();
                        }
                    }}
                >
                    <MobileNav navigation={navigation} />
                    <Flex flexDirection="column" justifyContent="flex-start">
                        <Flex bg="white" flexDirection="column" gap={4} px={4} py={space["6"]}>
                            {isLoggedIn ? (
                                <NextLink
                                    href={getPagePathByLocale(myAccountPage, locale)}
                                    leftIcon={<ECCOIcon name="account" />}
                                    aria-label={microcopies[CommonCMS.myAccount]}
                                    textDecoration="none"
                                    sx={linkSx}
                                    fontWeight={fontWeights.thin}
                                >
                                    {microcopies[CommonCMS.myAccount]}
                                </NextLink>
                            ) : (
                                <Box
                                    as="button"
                                    onClick={handleAccountClick}
                                    display="flex"
                                    alignItems="center"
                                    gap={2}
                                    aria-label={microcopies[CommonCMS.myAccount]}
                                    textDecoration="none"
                                    sx={{ cursor: "pointer", ...linkSx }}
                                >
                                    <ECCOIcon name="account" />
                                    {microcopies[CommonCMS.myAccount]}
                                </Box>
                            )}

                            <NextLink
                                href={getPagePathByLocale(storeLocatorPage, locale)}
                                leftIcon={<ECCOIcon name="store-locator" />}
                                aria-label={microcopies[ClickAndCollect.stores]}
                                textDecoration="none"
                                sx={linkSx}
                                fontWeight={fontWeights.thin}
                            >
                                {microcopies[ClickAndCollect.stores]}
                            </NextLink>

                            {isLoggedIn && (
                                <NextLink
                                    href={getPagePathByLocale(customerServicePages, locale)}
                                    leftIcon={<ECCOIcon name="help" />}
                                    aria-label={microcopies[CommonCMS.help]}
                                    textDecoration="none"
                                    sx={linkSx}
                                    fontWeight={fontWeights.thin}
                                >
                                    {microcopies[CommonCMS.help]}
                                </NextLink>
                            )}
                        </Flex>
                        <MobileBottomContainer
                            isAuthenticated={isLoggedIn}
                            session={session}
                            showModal={showModal}
                            onCloseMenu={onClose}
                        />
                    </Flex>
                </motion.div>

                {/* Mobile overlay */}
                <Portal>
                    <Box
                        position="fixed"
                        backgroundColor="rgba(0,0,0,0.6)"
                        opacity={isOpen ? 1 : 0}
                        visibility={isOpen ? "visible" : "hidden"}
                        top={0}
                        left={0}
                        right={0}
                        bottom={0}
                        zIndex={3}
                        transition="opacity 0.45s cubic-bezier(0.85, 0, 0.15, 1), visibility 0.45s cubic-bezier(0.85, 0, 0.15, 1)"
                        onClick={onClose}
                    />
                </Portal>
            </>
        </>
    );
};
